/**
 * Centralized error handling utilities for database operations
 * Provides consistent error handling patterns across all services
 */

export interface DatabaseError {
  code: string;
  message: string;
  details?: unknown;
  hint?: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  retryable?: boolean;
  userMessage?: string;
}

export interface ErrorHandlingResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: DatabaseError;
  errors?: DatabaseError[];
  retryAfter?: number;
}

/**
 * Standardized error codes for different types of database errors
 */
export const ERROR_CODES = {
  // Connection errors
  CONNECTION_FAILED: 'DB_CONNECTION_FAILED',
  TIMEOUT: 'DB_TIMEOUT',

  // Authentication/Authorization
  UNAUTHORIZED: 'DB_UNAUTHORIZED',
  FORBIDDEN: 'DB_FORBIDDEN',

  // Data validation
  VALIDATION_ERROR: 'DB_VALIDATION_ERROR',
  CONSTRAINT_VIOLATION: 'DB_CONSTRAINT_VIOLATION',
  FOREIGN_KEY_VIOLATION: 'DB_FOREIGN_KEY_VIOLATION',
  UNIQUE_VIOLATION: 'DB_UNIQUE_VIOLATION',

  // Data operations
  NOT_FOUND: 'DB_NOT_FOUND',
  DUPLICATE_ENTRY: 'DB_DUPLICATE_ENTRY',
  INVALID_INPUT: 'DB_INVALID_INPUT',

  // System errors
  INTERNAL_ERROR: 'DB_INTERNAL_ERROR',
  QUOTA_EXCEEDED: 'DB_QUOTA_EXCEEDED',
  CONFIGURATION_ERROR: 'SYS_CONFIGURATION_ERROR',

  // Business logic
  BUSINESS_RULE_VIOLATION: 'BL_RULE_VIOLATION',
  INSUFFICIENT_PERMISSIONS: 'BL_INSUFFICIENT_PERMISSIONS',
} as const;

/**
 * Maps Supabase error codes to our standardized error codes
 */
const SUPABASE_ERROR_MAP: Record<string, string> = {
  PGRST116: ERROR_CODES.NOT_FOUND,
  PGRST301: ERROR_CODES.UNAUTHORIZED,
  '23505': ERROR_CODES.UNIQUE_VIOLATION,
  '23503': ERROR_CODES.FOREIGN_KEY_VIOLATION,
  '23514': ERROR_CODES.CONSTRAINT_VIOLATION,
  '08006': ERROR_CODES.CONNECTION_FAILED,
  '57P01': ERROR_CODES.CONNECTION_FAILED,
  '08000': ERROR_CODES.CONNECTION_FAILED,
  '42501': ERROR_CODES.FORBIDDEN,
};

/**
 * User-friendly error messages for different error types
 */
const USER_MESSAGES: Record<string, string> = {
  [ERROR_CODES.CONNECTION_FAILED]:
    'Unable to connect to the database. Please check your internet connection and try again.',
  [ERROR_CODES.TIMEOUT]:
    'The operation took too long to complete. Please try again.',
  [ERROR_CODES.UNAUTHORIZED]:
    'You are not authorized to perform this action. Please log in and try again.',
  [ERROR_CODES.FORBIDDEN]: 'You do not have permission to perform this action.',
  [ERROR_CODES.NOT_FOUND]: 'The requested data was not found.',
  [ERROR_CODES.DUPLICATE_ENTRY]:
    'This entry already exists. Please use a different value.',
  [ERROR_CODES.VALIDATION_ERROR]:
    'The provided data is invalid. Please check your input and try again.',
  [ERROR_CODES.CONSTRAINT_VIOLATION]:
    'The operation violates data constraints. Please check your input.',
  [ERROR_CODES.FOREIGN_KEY_VIOLATION]:
    'This operation cannot be completed because it would affect related data.',
  [ERROR_CODES.UNIQUE_VIOLATION]:
    'This value must be unique. Please use a different value.',
  [ERROR_CODES.INTERNAL_ERROR]:
    'An internal error occurred. Please try again later.',
  [ERROR_CODES.QUOTA_EXCEEDED]:
    'Usage quota has been exceeded. Please try again later.',
  [ERROR_CODES.CONFIGURATION_ERROR]:
    'System configuration error. Please check your settings.',
  [ERROR_CODES.BUSINESS_RULE_VIOLATION]:
    'This operation violates business rules.',
  [ERROR_CODES.INSUFFICIENT_PERMISSIONS]:
    'You do not have sufficient permissions for this operation.',
};

/**
 * Determines if an error is retryable
 */
const RETRYABLE_ERRORS = new Set([
  ERROR_CODES.CONNECTION_FAILED,
  ERROR_CODES.TIMEOUT,
  ERROR_CODES.INTERNAL_ERROR,
  ERROR_CODES.QUOTA_EXCEEDED,
] as const);

/**
 * Error severity levels for different error types
 */
const ERROR_SEVERITY: Record<string, DatabaseError['severity']> = {
  [ERROR_CODES.CONNECTION_FAILED]: 'high',
  [ERROR_CODES.TIMEOUT]: 'medium',
  [ERROR_CODES.UNAUTHORIZED]: 'high',
  [ERROR_CODES.FORBIDDEN]: 'high',
  [ERROR_CODES.NOT_FOUND]: 'low',
  [ERROR_CODES.DUPLICATE_ENTRY]: 'medium',
  [ERROR_CODES.VALIDATION_ERROR]: 'medium',
  [ERROR_CODES.CONSTRAINT_VIOLATION]: 'medium',
  [ERROR_CODES.FOREIGN_KEY_VIOLATION]: 'medium',
  [ERROR_CODES.UNIQUE_VIOLATION]: 'medium',
  [ERROR_CODES.INTERNAL_ERROR]: 'critical',
  [ERROR_CODES.QUOTA_EXCEEDED]: 'high',
  [ERROR_CODES.BUSINESS_RULE_VIOLATION]: 'medium',
  [ERROR_CODES.INSUFFICIENT_PERMISSIONS]: 'high',
};

/**
 * Standardizes Supabase errors into our consistent format
 */
export function standardizeSupabaseError(error: {
  code?: string;
  message?: string;
  details?: unknown;
  hint?: string;
}): DatabaseError {
  const code = error.code || 'UNKNOWN';
  const standardCode = SUPABASE_ERROR_MAP[code] || ERROR_CODES.INTERNAL_ERROR;

  return {
    code: standardCode,
    message: error.message || 'Unknown database error',
    details: error.details,
    hint: error.hint,
    severity: ERROR_SEVERITY[standardCode] || 'medium',
    retryable: RETRYABLE_ERRORS.has(
      standardCode as (typeof ERROR_CODES)[keyof typeof ERROR_CODES]
    ),
    userMessage:
      USER_MESSAGES[standardCode] ||
      'An unexpected error occurred. Please try again.',
  };
}

/**
 * Creates a standardized error result
 */
export function createErrorResult<T = unknown>(
  error: DatabaseError | string,
  details?: unknown
): ErrorHandlingResult<T> {
  const dbError: DatabaseError =
    typeof error === 'string'
      ? {
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error,
          details,
          severity: 'medium',
          retryable: false,
          userMessage: 'An error occurred while processing your request.',
        }
      : error;

  return {
    success: false,
    error: dbError,
    retryAfter: dbError.retryable
      ? calculateRetryDelay(dbError.severity || 'medium')
      : undefined,
  };
}

/**
 * Creates a standardized success result
 */
export function createSuccessResult<T>(data: T): ErrorHandlingResult<T> {
  return {
    success: true,
    data,
  };
}

/**
 * Calculates retry delay based on error severity
 */
function calculateRetryDelay(severity: DatabaseError['severity']): number {
  switch (severity) {
    case 'low':
      return 1000; // 1 second
    case 'medium':
      return 5000; // 5 seconds
    case 'high':
      return 15000; // 15 seconds
    case 'critical':
      return 30000; // 30 seconds
    default:
      return 5000;
  }
}

/**
 * Handles database operations with consistent error handling
 */
export async function handleDatabaseOperation<T>(
  operation: () => Promise<T>,
  operationName: string = 'Database operation'
): Promise<ErrorHandlingResult<T>> {
  try {
    const result = await operation();
    return createSuccessResult(result);
  } catch (error: unknown) {
    console.error(`${operationName} failed:`, error);

    // Check if it's a Supabase error
    const errorObj = error as {
      code?: string;
      message?: string;
      details?: unknown;
      hint?: string;
    };
    if (errorObj.code || errorObj.message) {
      const standardError = standardizeSupabaseError(errorObj);
      return createErrorResult(standardError);
    }

    // Handle other types of errors
    if (error instanceof TypeError) {
      return createErrorResult({
        code: ERROR_CODES.INVALID_INPUT,
        message: error.message,
        severity: 'medium',
        retryable: false,
        userMessage:
          'Invalid input provided. Please check your data and try again.',
      });
    }

    if (error instanceof RangeError) {
      return createErrorResult({
        code: ERROR_CODES.VALIDATION_ERROR,
        message: error.message,
        severity: 'medium',
        retryable: false,
        userMessage:
          'Value is out of acceptable range. Please check your input.',
      });
    }

    // Generic error handling
    return createErrorResult(
      error instanceof Error ? error.message : 'Unknown error occurred',
      error
    );
  }
}

/**
 * Validates required fields and returns standardized error if missing
 */
export function validateRequiredFields(
  data: Record<string, unknown>,
  requiredFields: string[]
): DatabaseError | null {
  const missingFields = requiredFields.filter(
    (field) =>
      data[field] === undefined || data[field] === null || data[field] === ''
  );

  if (missingFields.length > 0) {
    return {
      code: ERROR_CODES.VALIDATION_ERROR,
      message: `Missing required fields: ${missingFields.join(', ')}`,
      details: { missingFields },
      severity: 'medium',
      retryable: false,
      userMessage: `Please provide values for: ${missingFields.join(', ')}`,
    };
  }

  return null;
}

/**
 * Logs errors consistently across the application
 */
export function logError(
  error: DatabaseError,
  context: {
    operation: string;
    userId?: string;
    propertyId?: string;
    additionalData?: Record<string, unknown>;
  }
): void {
  const logData = {
    timestamp: new Date().toISOString(),
    error: {
      code: error.code,
      message: error.message,
      severity: error.severity,
      retryable: error.retryable,
    },
    context,
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent : 'Server',
    url: typeof window !== 'undefined' ? window.location.href : 'Server',
  };

  // Log to console based on severity
  switch (error.severity) {
    case 'critical':
      console.error('CRITICAL ERROR:', logData);
      break;
    case 'high':
      console.error('HIGH SEVERITY ERROR:', logData);
      break;
    case 'medium':
      console.warn('MEDIUM SEVERITY ERROR:', logData);
      break;
    case 'low':
    default:
      console.info('LOW SEVERITY ERROR:', logData);
      break;
  }

  // In production, you would send this to your logging service
  // Example: sendToLoggingService(logData);
}

/**
 * Creates validation error for business rules
 */
export function createBusinessRuleError(
  ruleName: string,
  message: string,
  details?: Record<string, unknown>
): DatabaseError {
  return {
    code: ERROR_CODES.BUSINESS_RULE_VIOLATION,
    message: `Business rule violation: ${ruleName} - ${message}`,
    details: { ruleName, ...details },
    severity: 'medium',
    retryable: false,
    userMessage: message,
  };
}

/**
 * Aggregates multiple errors into a single result
 */
export function aggregateErrors<T = unknown>(
  errors: DatabaseError[]
): ErrorHandlingResult<T> {
  if (errors.length === 0) {
    return { success: true };
  }

  if (errors.length === 1) {
    return createErrorResult(errors[0]);
  }

  // Find the highest severity error
  const severityOrder = ['low', 'medium', 'high', 'critical'];
  const highestSeverity = errors.reduce((highest, error) => {
    const currentIndex = severityOrder.indexOf(error.severity || 'medium');
    const highestIndex = severityOrder.indexOf(highest.severity || 'medium');
    return currentIndex > highestIndex ? error : highest;
  });

  return {
    success: false,
    errors,
    error: {
      code: 'MULTIPLE_ERRORS',
      message: `Multiple errors occurred (${errors.length} total)`,
      severity: highestSeverity.severity,
      retryable: errors.some((e) => e.retryable),
      userMessage: `Multiple issues found: ${errors.map((e) => e.userMessage).join('; ')}`,
    },
  };
}
